import React, { useEffect, useRef, useState } from "react";
import { X, FileText, Loader2, ChevronDown } from "lucide-react";
import { TerminalTheme } from "../types";

type ScopedFetch = (path: string, init?: RequestInit) => Promise<Response>;

interface Props {
  filename: string;
  theme: TerminalTheme;
  scopedFetch?: ScopedFetch;
  onClose: () => void;
  /** When set, load raw markdown TEXT from this URL (a chat .md attachment
   *  at /api/uploads/chat/<name>) instead of the /api/documents JSON shape.
   *  iOS renders raw .md badly, so we route attachment .md files through
   *  this same in-app renderer. */
  rawUrl?: string;
}

// MobileDocViewerPane — full-screen modal that slides up from the bottom of
// the screen. Swipe-down to dismiss (touch threshold = 80px from origin).
// Used by /chat (Tailscale-companion mini-app) when the operator taps a
// doc-pill in an agent message.
//
// Mirrors the desktop DocViewerPane feature set: minimal markdown renderer,
// frontmatter strip + brand-voice lint badge when present. Wider chrome,
// bigger tap targets, single-column layout for phone use.

function parseFrontmatter(text: string): { fm: Record<string, string>; body: string } {
  if (!text.startsWith("---\n")) return { fm: {}, body: text };
  const end = text.indexOf("\n---\n", 4);
  if (end === -1) return { fm: {}, body: text };
  const header = text.slice(4, end);
  const body = text.slice(end + 5);
  const fm: Record<string, string> = {};
  for (const line of header.split("\n")) {
    const m = line.match(/^([A-Za-z_][A-Za-z0-9_-]*):\s*(.*)$/);
    if (m) {
      let v = m[2].trim();
      if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) v = v.slice(1, -1);
      fm[m[1]] = v;
    }
  }
  return { fm, body };
}

function renderMarkdown(md: string): React.ReactNode[] {
  const lines = md.split("\n");
  const out: React.ReactNode[] = [];
  let inList = false;
  let listItems: string[] = [];
  const flushList = (key: number) => {
    if (inList && listItems.length) {
      out.push(<ul key={`l${key}`} className="list-disc pl-6 my-2 text-[15px]">
        {listItems.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </ul>);
    }
    inList = false; listItems = [];
  };
  lines.forEach((raw, i) => {
    const ln = raw.trimEnd();
    if (/^#\s/.test(ln))      { flushList(i); out.push(<h1 key={i} className="text-2xl font-bold mt-4 mb-2">{ln.slice(2)}</h1>); }
    else if (/^##\s/.test(ln)){ flushList(i); out.push(<h2 key={i} className="text-xl font-bold mt-3 mb-2">{ln.slice(3)}</h2>); }
    else if (/^###\s/.test(ln)){flushList(i); out.push(<h3 key={i} className="text-lg font-bold mt-3 mb-1">{ln.slice(4)}</h3>); }
    else if (/^[-*]\s/.test(ln)){
      inList = true; listItems.push(ln.replace(/^[-*]\s/, ""));
    }
    else if (/^>\s/.test(ln)){flushList(i); out.push(<blockquote key={i} className="border-l-2 pl-3 my-2 opacity-80">{renderInline(ln.slice(2))}</blockquote>);}
    else if (!ln.trim())      { flushList(i); out.push(<div key={i} className="h-2" />); }
    else { flushList(i); out.push(<p key={i} className="text-[15px] my-1">{renderInline(ln)}</p>); }
  });
  flushList(lines.length);
  return out;
}

function renderInline(text: string): React.ReactNode {
  // Bold + italic + code + links — minimal pass
  const parts: React.ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2])      parts.push(<strong key={m.index}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>);
    else if (m[4]) parts.push(<code key={m.index} className="px-1 rounded bg-black/20 text-[13px]">{m[4]}</code>);
    else if (m[5]) parts.push(<a key={m.index} href={m[6]} target="_blank" rel="noopener noreferrer" className="underline">{m[5]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

export const MobileDocViewerPane: React.FC<Props> = ({ filename, theme, scopedFetch, onClose, rawUrl }) => {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [lintStatus, setLintStatus] = useState<"unknown" | "checking" | "pass" | "fail">("unknown");
  const [lintIssues, setLintIssues] = useState<number>(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);

  const fetcher: ScopedFetch = scopedFetch || ((p, init) => fetch(p, init));

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    // Chat .md attachment → fetch raw text. Document → fetch JSON shape.
    const load = rawUrl
      ? fetcher(rawUrl)
          .then(r => r.ok ? r.text() : Promise.reject(`HTTP ${r.status}`))
      : fetcher(`/api/documents/${encodeURIComponent(filename)}`)
          .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
          .then(d => d.markdown || d.content || "");
    load
      .then(md => { if (!cancelled) setMarkdown(typeof md === "string" ? md : ""); })
      .catch(e => { if (!cancelled) setErr(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filename, fetcher, rawUrl]);

  const { fm, body } = parseFrontmatter(markdown);

  // Brand-voice lint (when frontmatter exposes brand_voice_id)
  useEffect(() => {
    if (!fm.brand_voice_id || !body) return;
    setLintStatus("checking");
    fetcher(`/api/brand-voices/${encodeURIComponent(fm.brand_voice_id)}/lint`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: body }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(d => {
        const issues = Array.isArray(d?.issues) ? d.issues.length : 0;
        setLintIssues(issues);
        setLintStatus(issues === 0 ? "pass" : "fail");
      })
      .catch(() => setLintStatus("unknown"));
  }, [fm.brand_voice_id, body, fetcher]);

  // Swipe-down to dismiss
  const onTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - dragStartY.current;
    if (delta > 80) onClose();
    dragStartY.current = null;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end" onClick={onClose}>
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ backgroundColor: theme.bg, color: theme.fg }}
        className="w-full h-[92dvh] rounded-t-2xl flex flex-col overflow-hidden animate-[slideUp_200ms_ease-out]"
      >
        {/* Drag handle + header */}
        <div className="flex flex-col items-center pt-2 pb-1" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <div className="w-10 h-1 rounded-full" style={{ backgroundColor: theme.systemText, opacity: 0.4 }} />
          <div className="w-full flex items-center justify-between px-4 pt-2 pb-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FileText size={14} style={{ color: theme.accent }} />
              <span className="text-xs font-bold truncate" title={fm.title || filename} style={{ color: theme.fg }}>
                {fm.title || filename}
              </span>
            </div>
            {lintStatus === "pass" && (
              <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: "#16a34a30", color: "#16a34a" }}>✓ voice</span>
            )}
            {lintStatus === "fail" && (
              <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: "#f59e0b30", color: "#f59e0b" }}>{lintIssues} voice issues</span>
            )}
            <button onClick={onClose} aria-label="close" className="ml-2 p-2 -mr-2" style={{ color: theme.systemText }}>
              <X size={18} />
            </button>
          </div>
          {(fm.category || fm.status || fm.venue_id) && (
            <div className="w-full px-4 pb-2 flex items-center gap-2 text-[10px] uppercase tracking-wider flex-wrap" style={{ color: theme.systemText }}>
              {fm.category && <span>📁 {fm.category}</span>}
              {fm.status && <span>· {fm.status}</span>}
              {fm.venue_id && <span>· {fm.venue_id}</span>}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3" style={{ color: theme.fg }}>
          {loading && (
            <div className="flex items-center gap-2 text-sm" style={{ color: theme.systemText }}>
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          )}
          {err && (
            <div className="text-sm px-3 py-2 rounded" style={{ backgroundColor: "#d6243a20", color: "#d6243a" }}>
              {err}
            </div>
          )}
          {!loading && !err && (
            <div className="prose-mobile">
              {renderMarkdown(body || markdown)}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 text-[10px] flex items-center justify-center gap-1" style={{ color: theme.systemText, borderTop: `1px solid ${theme.border}` }}>
          <ChevronDown size={10} /> Swipe down or tap outside to close
        </div>
      </div>
    </div>
  );
};
